import { AlertTriangle, Plus, Save, Trash2, Users } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Spinner,
  useToast,
} from '../../ui';
import { useOwnersOfGoals } from '../../features/ownersOfGoals/useOwnersOfGoals';

/**
 * OwnersOfGoalsCard — an editable table defining the owner of each stream/goal.
 *
 * Reads/writes through the {@link useOwnersOfGoals} hook only (which seeds the
 * canonical record on first load). Supports inline edit, add-row, remove-row and
 * an explicit Save. Rendered as a card on the Task Templates page.
 */
export function OwnersOfGoalsCard() {
  const { toast } = useToast();
  const {
    entries,
    updatedBy,
    lastUpdated,
    loading,
    saving,
    error,
    problems,
    dirty,
    addRow,
    removeRow,
    editRow,
    save,
  } = useOwnersOfGoals();

  const onSave = async () => {
    const ok = await save();
    toast(
      ok
        ? { tone: 'success', title: 'Owners of goals saved' }
        : {
            tone: 'error',
            title: 'Save failed',
            description:
              problems[0] ?? 'Fix the highlighted rows and try again.',
          },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-accent" aria-hidden="true" />
          <CardTitle>Owners of Goals</CardTitle>
        </div>
        <Button
          size="sm"
          leftIcon={<Save className="h-3.5 w-3.5" />}
          loading={saving}
          disabled={!dirty || problems.length > 0}
          onClick={() => void onSave()}
        >
          Save
        </Button>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-zinc-500">
          Define who owns each stream / goal. Edit inline, add or remove rows,
          then Save.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading owners of goals" />
          </div>
        ) : error ? (
          <p className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            {error}
          </p>
        ) : (
          <>
            {problems.length > 0 && (
              <ul
                className="space-y-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                role="alert"
              >
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border border-surface-border bg-surface-2 px-3 py-1.5 text-left font-semibold text-zinc-100">
                      Stream / Goal
                    </th>
                    <th className="border border-surface-border bg-surface-2 px-3 py-1.5 text-left font-semibold text-zinc-100">
                      Owner
                    </th>
                    <th className="w-12 border border-surface-border bg-surface-2 px-3 py-1.5 text-left font-semibold text-zinc-100">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="border border-surface-border p-1 align-top">
                        <Input
                          aria-label="Stream or goal"
                          value={entry.stream}
                          onChange={(e) =>
                            editRow(entry.id, { stream: e.target.value })
                          }
                          placeholder="e.g. Velocity Goal"
                        />
                      </td>
                      <td className="border border-surface-border p-1 align-top">
                        <Input
                          aria-label="Owner"
                          value={entry.owner}
                          onChange={(e) =>
                            editRow(entry.id, { owner: e.target.value })
                          }
                          placeholder="e.g. Anutosh"
                        />
                      </td>
                      <td className="border border-surface-border p-1 text-center align-middle">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Remove row"
                          onClick={() => removeRow(entry.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="border border-surface-border px-3 py-6 text-center text-sm text-zinc-500"
                      >
                        No owners yet. Add the first row.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={addRow}
              >
                Add row
              </Button>
              {lastUpdated && (
                <span className="text-xs text-zinc-500">
                  Last saved by {updatedBy || 'unknown'}
                </span>
              )}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default OwnersOfGoalsCard;
